/**
 * Home Page - MMMS Main Dashboard
 * PRD Section 5.2: Dashboard/Home Page
 */
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants';
import { useWeeklyReportsList, useTechnicalAssessmentsList, usePartsRequisitionsList } from '@/api';
import { Card, CardTitle, Button, Spinner } from '@/components/ui';
import styles from './Home.module.css';

// Icons (using FontAwesome classes)
const ICONS = {
    weeklyReport: 'fa-solid fa-clipboard-list',
    technicalAssessment: 'fa-solid fa-wrench',
    partsRequisition: 'fa-solid fa-box-open',
    reports: 'fa-solid fa-folder-open',
    equipment: 'fa-solid fa-truck-monster',
    clock: 'fa-solid fa-clock',
    check: 'fa-solid fa-check-circle',
    pending: 'fa-solid fa-hourglass-half',
    alert: 'fa-solid fa-exclamation-triangle',
};

export function Home() {
    const navigate = useNavigate();

    // Fetch recent reports
    const { data: weeklyReports, isLoading: loadingWeekly } = useWeeklyReportsList();
    const { data: assessments, isLoading: loadingAssessments } = useTechnicalAssessmentsList();
    const { data: requisitions, isLoading: loadingRequisitions } = usePartsRequisitionsList();

    const isLoading = loadingWeekly || loadingAssessments || loadingRequisitions;

    // Calculate stats safely
    const stats = {
        weeklyReports: Array.isArray(weeklyReports) ? weeklyReports.length : 0,
        pendingReports: Array.isArray(weeklyReports) ? weeklyReports.filter(r => r.status === 'submitted').length : 0,
        assessments: Array.isArray(assessments) ? assessments.length : 0,
        requisitions: Array.isArray(requisitions) ? requisitions.length : 0,
    };

    // Get recent reports (last 5) safely
    const recentWeekly = Array.isArray(weeklyReports) ? weeklyReports.slice(0, 5) : [];
    const recentAssessments = Array.isArray(assessments) ? assessments.slice(0, 5) : [];
    const recentRequisitions = Array.isArray(requisitions) ? requisitions.slice(0, 5) : [];

    return (
        <div className={styles.dashboard}>
            {/* Header */}
            <header className={styles.header}>
                <h1 className={styles.title}>Dashboard de Gestión</h1>
                <p className={styles.subtitle}>Sistema de Gestión de Mantenimiento Minero</p>
            </header>

            {/* Quick Actions */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Acciones Rápidas</h2>
                <div className={styles.quickActions}>
                    <Card className={styles.actionCard} onClick={() => navigate(ROUTES.weeklyReport)}>
                        <div className={styles.actionIcon}>
                            <i className={ICONS.weeklyReport}></i>
                        </div>
                        <CardTitle>Reporte Semanal</CardTitle>
                        <p className={styles.actionDescription}>Presiona para llenar reporte de estado semanal de operatividad de los activos</p>
                    </Card>

                    <Card className={styles.actionCard} onClick={() => navigate(ROUTES.technicalAssessment)}>
                        <div className={styles.actionIcon}>
                            <i className={ICONS.technicalAssessment}></i>
                        </div>
                        <CardTitle>Evaluación Técnica</CardTitle>
                        <p className={styles.actionDescription}>Presiona para llenar reporte de evaluación detallada de sistemas y componentes</p>
                    </Card>

                    <Card className={styles.actionCard} onClick={() => navigate(ROUTES.partsRequisition)}>
                        <div className={styles.actionIcon}>
                            <i className={ICONS.partsRequisition}></i>
                        </div>
                        <CardTitle>Pedido de Repuestos</CardTitle>
                        <p className={styles.actionDescription}>Presiona para llenar reporte de solicitud de materiales y repuestos para taller</p>
                    </Card>
                </div>
            </section>

            {/* Stats Cards */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>Resumen General</h2>
                {isLoading ? (
                    <div className={styles.loadingContainer}>
                        <Spinner size="lg" />
                    </div>
                ) : (
                    <div className={styles.statsGrid}>
                        <Card className={styles.statCard}>
                            <div className={styles.statIcon} data-color="primary">
                                <i className={ICONS.weeklyReport}></i>
                            </div>
                            <div className={styles.statContent}>
                                <span className={styles.statValue}>{stats.weeklyReports}</span>
                                <span className={styles.statLabel}>Total Reportes</span>
                            </div>
                        </Card>

                        <Card className={styles.statCard}>
                            <div className={styles.statIcon} data-color="warning">
                                <i className={ICONS.pending}></i>
                            </div>
                            <div className={styles.statContent}>
                                <span className={styles.statValue}>{stats.pendingReports}</span>
                                <span className={styles.statLabel}>Pendientes</span>
                            </div>
                        </Card>

                        <Card className={styles.statCard}>
                            <div className={styles.statIcon} data-color="info">
                                <i className={ICONS.technicalAssessment}></i>
                            </div>
                            <div className={styles.statContent}>
                                <span className={styles.statValue}>{stats.assessments}</span>
                                <span className={styles.statLabel}>Evaluaciones</span>
                            </div>
                        </Card>

                        <Card className={styles.statCard}>
                            <div className={styles.statIcon} data-color="success">
                                <i className={ICONS.partsRequisition}></i>
                            </div>
                            <div className={styles.statContent}>
                                <span className={styles.statValue}>{stats.requisitions}</span>
                                <span className={styles.statLabel}>Pedidos</span>
                            </div>
                        </Card>
                    </div>
                )}
            </section>

            {/* Recent Reports */}
            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>Reportes Recientes</h2>
                    <Button variant="secondary" size="sm" onClick={() => navigate(ROUTES.reportsLibrary)}>
                        Ver Biblioteca
                    </Button>
                </div>

                <div className={styles.reportsGrid}>
                    {/* Weekly Reports */}
                    <Card className={styles.recentCard}>
                        <CardTitle variant="simple" >Reportes Semanales</CardTitle>
                        {loadingWeekly ? (
                            <Spinner size="sm" />
                        ) : recentWeekly.length === 0 ? (
                            <p className={styles.emptyMessage}>No hay reportes semanales registrados</p>
                        ) : (
                            <ul className={styles.reportList}>
                                {recentWeekly.map(report => (
                                    <li key={report.id} className={styles.reportItem}>
                                        <span className={styles.reportDate}>{report.week_start}</span>
                                        <span className={`${styles.reportStatus} ${styles[report.status]}`}>
                                            {report.status === 'draft' ? 'Borrador' :
                                                report.status === 'submitted' ? 'Enviado' :
                                                    report.status === 'approved' ? 'Aprobado' : 'Rechazado'}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Card>

                    {/* Technical Assessments */}
                    <Card className={styles.recentCard}>
                        <CardTitle variant="simple" >Evaluaciones Técnicas</CardTitle>
                        {loadingAssessments ? (
                            <Spinner size="sm" />
                        ) : recentAssessments.length === 0 ? (
                            <p className={styles.emptyMessage}>No hay evaluaciones registradas</p>
                        ) : (
                            <ul className={styles.reportList}>
                                {recentAssessments.map(assessment => (
                                    <li key={assessment.id} className={styles.reportItem}>
                                        <span className={styles.reportTitle}>{assessment.title}</span>
                                        <span className={`${styles.reportStatus} ${styles[assessment.status]}`}>
                                            {assessment.status === 'draft' ? 'Borrador' :
                                                assessment.status === 'submitted' ? 'Enviado' :
                                                    assessment.status === 'approved' ? 'Aprobado' : 'Rechazado'}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Card>

                    {/* Parts Requisitions */}
                    <Card className={styles.recentCard}>
                        <CardTitle variant="simple" >Pedidos de Repuestos</CardTitle>
                        {loadingRequisitions ? (
                            <Spinner size="sm" />
                        ) : recentRequisitions.length === 0 ? (
                            <p className={styles.emptyMessage}>No hay pedidos registrados</p>
                        ) : (
                            <ul className={styles.reportList}>
                                {recentRequisitions.map(req => (
                                    <li key={req.id} className={styles.reportItem}>
                                        <span className={styles.reportTitle}>{req.equipment_code}</span>
                                        <span className={`${styles.reportStatus} ${styles[req.status]}`}>
                                            {req.status === 'draft' ? 'Borrador' :
                                                req.status === 'submitted' ? 'Enviado' :
                                                    req.status === 'approved' ? 'Aprobado' : 'Rechazado'}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Card>
                </div>
            </section>
        </div>
    );
}
